import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ItemsSelectList } from "../../../components";
import { SearchBar, Button } from "shared";
import { useModalContext } from "../../../../../shared/components/Modal/context/modal.context";
import { ConfigurationChangesForm } from "../configuration-changes-form/configuration-changes-form";
import { useInventoryActionsContext } from "domain/Inventory/context";
import "./create-new-inventory-part.form.scss";

export const CreateNewInventoryPartForm = ({ onSubmit, onCancel, inventory, isBtnLoading }) => {
  const { id: inventoryId, label: inventoryLabel } = inventory;
  const [newInventoryParts, setNewInventoryParts] = useState([]);
  const [selectedPartsDetails, setSelectedPartsDetails] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");

  const { getParts, validateAllItems, getParentById, getAllItems } = useInventoryActionsContext();
  const { createModal } = useModalContext();

  const hasMore = searchResults.length < totalResults;

  const {
    element: CreateNewInventoryPartModal,
    open,
    close,
  } = useMemo(() => createModal({ large: true }), [createModal]);

  const fetchAndSetParts = useCallback(
    (id, term, page) => {
      setIsSearching(true);
      getParts(id, term, page)
        .then((response) => {
          const { data, total } = response;
          setSearchResults((prev) => (page === 1 ? data : [...prev, ...data]));
          setTotalResults(total);
        })
        .catch((err) => {
          console.error("Error in getParts:", err);
        })
        .finally(() => {
          setIsSearching(false);
        });
    },
    [getParts]
  );

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchAndSetParts(inventoryId, currentSearchTerm, nextPage);
  };

  const onSearchQueryChange = (value) => {
    setCurrentSearchTerm(value);
    setCurrentPage(1);
    fetchAndSetParts(inventoryId, value, 1);
  };

  useEffect(() => {
    fetchAndSetParts(inventoryId, "", 1);
  }, [fetchAndSetParts, inventoryId]);

  const removeItem = (item) => setNewInventoryParts((parts) => parts.filter((part) => part.itemId !== item.itemId));
  const addItem = (value) => setNewInventoryParts((prev) => [...prev, value]);

  useEffect(() => {
    if (newInventoryParts.length > 0) {
      getAllItems(newInventoryParts)
        .then((data) => setSelectedPartsDetails(data))
        .catch((err) => alert(err));
    } else {
      setSelectedPartsDetails([]);
    }
  }, [newInventoryParts, getAllItems]);

  const onConfirm = (e) => {
    e.preventDefault();
    onSubmit({ inventory, inventoryPart: selectedPartsDetails });
  };

  const openModal = async (e) => {
    e.preventDefault();
    if (!selectedPartsDetails.length) return alert("Morate odabrati deo za dodavanje!");
    if (await getParentById(inventoryId))
      return alert("Trenutno nije podržano dodavanje delova u predmet koji je deo drugog predmeta.");

    const partsWithSubParts = selectedPartsDetails.filter((item) => item.parts.length > 0);
    if (partsWithSubParts.length) {
      const partLabels = partsWithSubParts.map((part) => part.label).join(", ");
      return alert(`Trenutno nije podržano dodavanje predmeta koji sadrži delove: ${partLabels}`);
    }

    validateAllItems(inventoryId, selectedPartsDetails)
      .then(open)
      .catch((error) => alert(error.message));
  };

  return (
    <>
      <div>
        <div className="create-inventory-item">
          <h2 className="create-inventory-item__title">
            <span>Dodavanje delova na predmet</span>
            <br />
            {inventoryLabel}
          </h2>
          <div className="create-inventory-item__search_section">
            <div className="create-inventory-item__search">
              <SearchBar
                onChange={onSearchQueryChange}
                placeholder="Pretraži"
                autoComplete="off"
                spellCheck="false"
                id="searchPart"
              />
            </div>
          </div>
          <ItemsSelectList
            data={searchResults}
            addItem={addItem}
            removeItem={removeItem}
            selectedItems={newInventoryParts}
            totalResults={totalResults}
            hasMore={hasMore}
            isSearching={isSearching}
            onLoadMore={handleLoadMore}
          />
        </div>
        <div className="create-inventory-item__controls">
          <Button buttonText={"Otkaži"} buttonColor={"gray"} event={onCancel} />
          <Button
            type="submit"
            isDisabled={!newInventoryParts.length}
            buttonText={"Dodaj"}
            buttonColor={"green"}
            event={openModal}
            spinnerColor={"light"}
            isLoading={isBtnLoading}
          />
        </div>
      </div>
      <CreateNewInventoryPartModal>
        <ConfigurationChangesForm
          onSubmit={onConfirm}
          validation
          onCancel={close}
          data={{ inventory: inventory, inventoryParts: selectedPartsDetails }}
        />
      </CreateNewInventoryPartModal>
    </>
  );
};
